import type { BuildPieceDef, SupportBehavior } from './catalog';
import { pieceDef, SUPPORT } from './catalog';
import type { PlacedPiece } from './types';

// The support graph over placed instances. Grounded foundations seed 1.0; every other piece takes
// its best parent's value minus the loss of crossing that edge. The edge loss is PARENT-AWARE for
// floors/ceilings: resting on a vertical support beneath you (wall/pillar/foundation) barely loses,
// but hanging off a neighbouring floor/ceiling cantilevers and decays fast — so an unsupported roof
// can only reach ~1 tile past its walls before it needs a pillar. Parasites (door/stairs/cupboard)
// inherit their host and never collapse alone. recompute() relaxes values outward from the grounded
// seeds; any piece that falls to <= 0 is a crumble candidate (handed to crumble.ts by the facade).

function lossOf(support: SupportBehavior): number {
  switch (support.kind) {
    case 'grounded': return 0;
    case 'adjacentFoundation': return support.loss;
    case 'wallUp': return support.loss;
    case 'floorConsume': return support.loss;
    case 'roofConsume': return support.loss;
    case 'pillar': return support.loss;
    case 'parasite': return 0;
  }
}

function isGrounded(def: BuildPieceDef): boolean { return def.support.kind === 'grounded'; }
function isParasite(def: BuildPieceDef): boolean { return def.support.kind === 'parasite'; }
function groundedBase(def: BuildPieceDef): number { return def.support.kind === 'grounded' ? def.support.base : 1.0; }

// Pieces that carry load straight down to the ground: foundations, walls, pillars.
function isVerticalSupport(kind: SupportBehavior['kind']): boolean {
  return kind === 'grounded' || kind === 'adjacentFoundation' || kind === 'wallUp' || kind === 'pillar';
}

// Support lost crossing one edge from `parent` down to `child`. A floor/ceiling on a vertical support
// directly beneath it barely loses (floorOnSupport); one only hanging off a neighbouring floor/
// ceiling is a cantilever (large loss). Everything else uses its own fixed loss.
function hopLoss(parentDef: BuildPieceDef, childDef: BuildPieceDef): number {
  const ck = childDef.support.kind;
  if (ck === 'parasite') return 0;
  if (ck === 'floorConsume' || ck === 'roofConsume') {
    return isVerticalSupport(parentDef.support.kind) ? SUPPORT.floorOnSupport : SUPPORT.cantilever;
  }
  return lossOf(childDef.support);
}

export class Stability {
  // The graph is read from the live placed map the facade owns; we keep only the parent/child edges.
  private readonly placed: Map<string, PlacedPiece>;

  constructor(placed: Map<string, PlacedPiece>) {
    this.placed = placed;
  }

  // Register a freshly-placed piece: its `supports[]` (parents) are already resolved by the facade
  // (the instances it rests on). Compute an initial value, then recompute the whole graph so the
  // back-edges and propagation stay consistent.
  register(piece: PlacedPiece): void {
    piece.supportValue = this.seedValue(piece);
    this.recompute();
  }

  // Best value a piece `def` would receive from a set of parent ids, plus the dominant parent. Each
  // edge's loss is computed from the parent's piece type (so a wall under a ceiling barely costs, a
  // neighbouring ceiling costs a cantilever). Parasites inherit the best parent with no loss.
  private bestParent(parentIds: string[], def: BuildPieceDef): { value: number; dominant?: string } {
    let best = 0;
    let dominant: string | undefined;
    for (const pid of parentIds) {
      const parent = this.placed.get(pid);
      if (!parent) continue;
      const cand = isParasite(def) ? parent.supportValue : parent.supportValue - hopLoss(pieceDef(parent.defId), def);
      if (cand > best) { best = cand; dominant = pid; }
    }
    return { value: Math.max(0, best), dominant };
  }

  // The value a piece would get from its current parents (before a global relax).
  private seedValue(piece: PlacedPiece): number {
    const def = pieceDef(piece.defId);
    if (isGrounded(def)) return groundedBase(def);
    return this.bestParent(piece.supports, def).value;
  }

  // Project the support value a CANDIDATE piece would receive from a given parent set, WITHOUT
  // inserting it. Used by the validator's NO_SUPPORT gate. Grounded always passes (base).
  project(defId: PlacedPiece['defId'], parentIds: string[]): number {
    const def = pieceDef(defId);
    if (isGrounded(def)) return groundedBase(def);
    const { value } = this.bestParent(parentIds, def);
    if (isParasite(def)) return value > 0 ? value : 1.0; // lone parasite on a terrain-grounded floor seeds full
    return value;
  }

  // Relax the whole graph from grounded seeds outward: a child's value is best(parent - edge loss).
  // Iterates until stable (bounded passes, like Bellman-Ford).
  recompute(): void {
    for (const p of this.placed.values()) {
      const def = pieceDef(p.defId);
      p.supportValue = isGrounded(def) ? groundedBase(def) : 0;
    }
    const n = this.placed.size;
    for (let pass = 0; pass < n; pass++) {
      let changed = false;
      for (const p of this.placed.values()) {
        const def = pieceDef(p.defId);
        if (isGrounded(def)) continue;
        const { value, dominant } = this.bestParent(p.supports, def);
        if (value > p.supportValue + 1e-6) {
          p.supportValue = value;
          p.supportedBy = dominant;
          changed = true;
        }
      }
      if (!changed) break;
    }
  }

  // Pieces whose support has dropped to <= 0 (crumble candidates). Parasites are excluded — they
  // ride their host and are removed with it, never on their own.
  collapsed(): PlacedPiece[] {
    const out: PlacedPiece[] = [];
    for (const p of this.placed.values()) {
      const def = pieceDef(p.defId);
      if (isParasite(def) || isGrounded(def)) continue;
      if (p.supportValue <= 0) out.push(p);
    }
    return out;
  }
}
